import { CALL_RECORDING_STATE_LABELS, type CallConsent, type CallRecordingState } from '@lia/contracts';
import { HeadphonesIcon } from '@hugeicons/core-free-icons';
import { formatDateTime, formatDuration } from '../../lib/format.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Icon } from '../primitives/icon.js';
import { InlineNotice } from '../primitives/notice.js';

// Recording section of a call (plan B4). Presentational: the app passes the server's honest state; the player only
// appears for a recording the caller may hear, and plays the URL the server returned (LIA's own authenticated route
// for stored recordings — never a provider URL; Aircall keeps its temporary-link flow).

export const CALL_RECORDING_STATE_TONE: Record<CallRecordingState, StatusTone> = {
  provider: 'info',
  none: 'neutral',
  disabled: 'neutral',
  pending: 'info',
  no_consent: 'warning',
  processing: 'info',
  available: 'success',
  failed: 'danger',
  deleted: 'neutral',
};

export interface CallRecordingPanelProps {
  state: CallRecordingState;
  detail: string;
  canAccess: boolean;
  consent: CallConsent | null;
  durationSeconds: number | null;
  timezone: string;
  /** URL returned by the server once the user asked to listen (null until then). */
  audioUrl: string | null;
  loading?: boolean;
  error?: string | null;
  onListen?: () => void;
  headingId?: string;
}

function ConsentBlock({ consent, timezone }: { consent: CallConsent; timezone: string }) {
  const status =
    consent.status === 'played'
      ? `Aviso reproducido${consent.playedAt ? ` · ${formatDateTime(consent.playedAt, timezone)}` : ''}`
      : consent.status === 'failed'
        ? 'Aviso no reproducido: no se grabó'
        : 'Aviso pendiente';
  return (
    <div className="grid gap-1 rounded-card border border-border-subtle p-3">
      <p className="text-meta font-medium text-text-secondary">Aviso de consentimiento</p>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={consent.status === 'played' ? 'success' : consent.status === 'failed' ? 'warning' : 'info'}>{status}</StatusBadge>
      </div>
      {consent.noticeText ? <p className="text-body text-text-primary">«{consent.noticeText}»</p> : null}
      {consent.detail ? <p className="text-meta text-text-secondary">{consent.detail}</p> : null}
    </div>
  );
}

export function CallRecordingPanel({ state, detail, canAccess, consent, durationSeconds, timezone, audioUrl, loading, error, onListen, headingId = 'call-recording' }: CallRecordingPanelProps) {
  const playable = (state === 'available' || state === 'provider') && canAccess;
  return (
    <section className="grid gap-2" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={headingId} className="text-body font-semibold text-text-primary">
          Grabación
        </h3>
        <StatusBadge tone={CALL_RECORDING_STATE_TONE[state]}>{CALL_RECORDING_STATE_LABELS[state]}</StatusBadge>
        {state === 'available' && durationSeconds !== null ? <span className="text-meta text-text-secondary">{formatDuration(durationSeconds)}</span> : null}
      </div>
      <p className="text-meta text-text-secondary">{detail}</p>
      {consent ? <ConsentBlock consent={consent} timezone={timezone} /> : null}
      {playable ? (
        audioUrl ? (
          <audio controls preload="none" src={audioUrl} className="w-full" aria-label="Reproductor de la grabación">
            <track kind="captions" />
          </audio>
        ) : onListen ? (
          <div>
            <Button size="sm" variant="secondary" leadingIcon={<Icon icon={HeadphonesIcon} aria-hidden />} loading={loading} onClick={onListen}>
              Escuchar grabación
            </Button>
          </div>
        ) : null
      ) : null}
      {error ? <InlineNotice tone="warning">{error}</InlineNotice> : null}
    </section>
  );
}
